// src/styles/theme.js

/* 
=== PREVIOUS THEME BACKUP ===
If you want to revert to the previous theme, uncomment this section and comment out the new theme below:

export const previousTheme = {
  colors: {
    // Primary BSides colors
    primary: {
      blue: '#0080D6',
      yellow: '#FFE500',
      cyan: '#00D4FF',
      purple: '#9B59B6',
    },
    
    // Background colors inspired by reference image
    background: {
      primary: '#0A0A0F',
      secondary: '#1A1A2E',
      tertiary: '#16213E',
      gradient: 'linear-gradient(135deg, #0A0A0F 0%, #1A1A2E 50%, #2D1B69 100%)',
    },
    
    // Text colors
    text: {
      primary: '#FFFFFF',
      secondary: '#B0C4DE',
      muted: '#6B7280',
      accent: '#FFE500',
    },
    
    // UI elements
    border: 'rgba(255, 255, 255, 0.1)',
    surface: 'rgba(255, 255, 255, 0.05)',
    glow: 'rgba(155, 89, 182, 0.3)',
  },
  
  gradients: {
    primary: 'linear-gradient(135deg, #FFE500 0%, #0080D6 100%)',
    cyber: 'linear-gradient(135deg, #9B59B6 0%, #0080D6 50%, #00D4FF 100%)',
    hero: 'linear-gradient(135deg, #0A0A0F 0%, #2D1B69 50%, #9B59B6 100%)',
    card: 'linear-gradient(135deg, rgba(155, 89, 182, 0.1) 0%, rgba(0, 128, 214, 0.1) 100%)',
  },
  
  shadows: {
    glow: '0 0 30px rgba(155, 89, 182, 0.5)',
    card: '0 8px 32px rgba(0, 0, 0, 0.3)',
    button: '0 4px 20px rgba(255, 229, 0, 0.3)',
  },
  
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '4rem',
    xxxl: '6rem',
  },
  
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px',
  },
  
  fonts: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', monospace",
  },
  
  zIndex: {
    background: -1,
    content: 1,
    header: 1000,
    modal: 2000,
  },
  
  animations: {
    duration: {
      fast: '0.2s',
      normal: '0.3s',
      slow: '0.5s',
    },
    easing: {
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
  },
  cssVariables: {
    '--primary-blue': '#0080D6',
    '--primary-yellow': '#FFE500',
    '--primary-cyan': '#00D4FF',
    '--surface': '#1A1A2E',
    '--background': '#0F0F1E',
    '--text-primary': '#FFFFFF',
    '--text-secondary': '#B0C4DE',
    '--border': '#333366',
    
    // Animation durations
    '--animation-fast': '0.2s',
    '--animation-normal': '0.3s',
    '--animation-slow': '0.6s',
    
    // Easing functions
    '--ease-out-quad': 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '--ease-out-cubic': 'cubic-bezier(0.215, 0.61, 0.355, 1)',
    '--ease-spring': 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  }
};
*/

// === NEW THEME MATCHING ILLUSTRATION ===
export const theme = {
  colors: {
    // Primary colors from your illustration and BSides logo
    primary: {
      blue: '#0080D6',        // BSides blue from your logo
      yellow: '#FFE500',      // Bright yellow from logo and illustration
      cyan: '#00FFFF',        // Cyan from shield glow in illustration
      purple: '#8000FF',      // Deep purple from illustration background
      green: '#00FF88',       // Matrix green from code elements
      magenta: '#FF00FF',     // Cyberpunk accent color
    },
    
    // Background colors matching illustration's cyberpunk aesthetic
    background: {
      primary: '#0A0015',     // Very dark purple-black (matches illustration base)
      secondary: '#1A0030',   // Dark purple (illustration mid-tone)
      tertiary: '#2D1B4E',    // Purple-blue (illustration highlights)
      surface: '#16213E',     // Card/surface color
      // Matches the exact gradient from your illustration
      gradient: 'linear-gradient(180deg, #0a0015 0%, #1a0030 30%, #2d1b4e 100%)',
    },
    
    // Text colors for better contrast with new background
    text: {
      primary: '#FFFFFF',     // Pure white for main text
      secondary: '#E0E6ED',   // Light blue-white for secondary text
      muted: '#A0AEC0',       // Muted blue-gray for less important text
      accent: '#FFE500',      // Yellow accent for highlights
      cyber: '#00FFFF',       // Cyan for tech/code elements
    },
    
    // UI elements matching illustration style
    border: 'rgba(0, 255, 255, 0.2)',  // Cyan border with transparency
    surface: 'rgba(0, 212, 255, 0.05)', // Subtle cyan surface tint
    glow: 'rgba(0, 255, 255, 0.4)',     // Cyan glow effect
    warning: '#FF4444',                  // Red for alerts/warnings
    success: '#00FF88',                  // Matrix green for success
  },
  
  gradients: {
    // Primary gradient matching logo colors
    primary: 'linear-gradient(135deg, #FFE500 0%, #0080D6 100%)',
    
    // Cyberpunk gradients from illustration
    cyber: 'linear-gradient(135deg, #8000FF 0%, #0080D6 50%, #00FFFF 100%)',
    shield: 'linear-gradient(135deg, #00FFFF 0%, #0080FF 30%, #8000FF 70%, #4B0082 100%)',
    
    // Background gradients
    hero: 'linear-gradient(180deg, #0a0015 0%, #1a0030 30%, #2d1b4e 100%)',
    card: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(128, 0, 255, 0.1) 100%)',
    
    // Button gradients
    button: 'linear-gradient(135deg, #FFE500 0%, #FFA500 100%)',
    buttonSecondary: 'linear-gradient(135deg, #00FFFF 0%, #0080D6 100%)',
    
    // Special effects
    neon: 'linear-gradient(90deg, transparent, #00FFFF, transparent)',
    matrix: 'linear-gradient(180deg, #00FF88 0%, #00D4AA 100%)',
  },
  
  shadows: {
    // Cyberpunk glow effects matching illustration
    glow: '0 0 30px rgba(0, 255, 255, 0.5)',
    glowStrong: '0 0 50px rgba(0, 255, 255, 0.7)',
    glowYellow: '0 0 30px rgba(255, 229, 0, 0.6)',
    glowPurple: '0 0 30px rgba(128, 0, 255, 0.5)',
    
    // UI shadows
    card: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 255, 255, 0.1)',
    button: '0 4px 20px rgba(255, 229, 0, 0.4)',
    buttonHover: '0 6px 30px rgba(255, 229, 0, 0.6)',
    
    // Depth shadows
    elevated: '0 12px 40px rgba(0, 0, 0, 0.3)',
    floating: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  
  // Keep existing spacing, breakpoints, fonts
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '4rem',
    xxxl: '6rem',
  },
  
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1200px',
  },
  
  fonts: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
    cyber: "'Orbitron', 'Arial', sans-serif", // Cyberpunk-style font
  },
  
  zIndex: {
    background: -1,
    content: 1,
    header: 1000,
    modal: 2000,
    tooltip: 3000,
  },
  
  animations: {
    duration: {
      fast: '0.2s',
      normal: '0.3s',
      slow: '0.6s',
      cyber: '1.5s',     // For cyberpunk effects
    },
    easing: {
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      cyber: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
  },
  
  // Enhanced CSS variables for cyberpunk effects
  cssVariables: {
    // Colors
    '--primary-blue': '#0080D6',
    '--primary-yellow': '#FFE500',
    '--primary-cyan': '#00FFFF',
    '--primary-purple': '#8000FF',
    '--primary-green': '#00FF88',
    '--primary-magenta': '#FF00FF',
    
    // Backgrounds
    '--bg-primary': '#0A0015',
    '--bg-secondary': '#1A0030',
    '--bg-tertiary': '#2D1B4E',
    '--surface': '#16213E',
    
    // Text
    '--text-primary': '#FFFFFF',
    '--text-secondary': '#E0E6ED',
    '--text-muted': '#A0AEC0',
    '--text-accent': '#FFE500',
    '--text-cyber': '#00FFFF',
    
    // Effects
    '--border-cyber': 'rgba(0, 255, 255, 0.2)',
    '--glow-cyan': 'rgba(0, 255, 255, 0.4)',
    '--glow-yellow': 'rgba(255, 229, 0, 0.6)',
    '--glow-purple': 'rgba(128, 0, 255, 0.5)',
    
    // Animation durations
    '--animation-fast': '0.2s',
    '--animation-normal': '0.3s',
    '--animation-slow': '0.6s',
    '--animation-cyber': '1.5s',
    
    // Easing functions
    '--ease-out-quad': 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '--ease-out-cubic': 'cubic-bezier(0.215, 0.61, 0.355, 1)',
    '--ease-spring': 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    '--ease-cyber': 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  
  // Special cyberpunk theme additions
  cyberpunk: {
    // Neon colors for special effects
    neon: {
      cyan: '#00FFFF',
      yellow: '#FFE500',
      purple: '#8000FF',
      green: '#00FF88',
      magenta: '#FF00FF',
      red: '#FF4444',
    },
    
    // Glow intensities
    glow: {
      subtle: '0 0 10px',
      normal: '0 0 20px',
      strong: '0 0 40px',
      intense: '0 0 60px',
    },
    
    // Animation keyframes for cyberpunk effects
    effects: {
      scan: 'scan 2s linear infinite',
      flicker: 'flicker 0.15s ease-in-out infinite alternate',
      pulse: 'pulse 2s ease-in-out infinite',
      matrix: 'matrix 6s linear infinite',
    },
  }
};

// Export previous theme as backup
export const previousTheme = {
  // ... (previous theme would be here for easy reverting)
};